/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';

declare let gtag: (...args: any[]) => void;
declare let clarity: (...args: any[]) => void;

@Injectable({
  providedIn: 'root',
})
export class Analytics {
  private readonly TAG_ID = 'G-DB12Z63N84';
  private started = false;
  private startClarityOnce() {
    if (this.started) return;
    this.started = true;
    clarity('consent', true);
    clarity('start');
  }
  private copyListenerBound = false;
  private lastCopyTs = 0;
  private readonly COPY_DEBOUNCE_MS = 500; // กันสแปม 0.5s/ครั้ง

  private getCopiedTextFromEvent(e: ClipboardEvent): string {
    // 1) จาก clipboardData (ถ้ามี)
    const fromClipboard = e.clipboardData?.getData('text') ?? '';
    if (fromClipboard && fromClipboard.length > 0) return fromClipboard;

    // 2) จาก window.getSelection()
    try {
      const sel = window.getSelection?.();
      const txt = sel?.toString() ?? '';
      return txt;
    } catch {
      return '';
    }
  }
  private bindGlobalCopyListener(): void {
    if (this.copyListenerBound) return;
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const onCopy = (e: ClipboardEvent) => {
      if (!this.isExamSession()) return;
      const now = Date.now();
      if (now - this.lastCopyTs < this.COPY_DEBOUNCE_MS) return; // debounce
      this.lastCopyTs = now;

      try {
        const MAX_LEN = 20000;
        const text = this.getCopiedTextFromEvent(e);
        const copiedLength = Math.min((text ?? '').length, MAX_LEN);

        // ถ้ายาว 0 ไม่ต้องส่ง
        if (copiedLength <= 0) return;

        // ส่งเข้า pipeline ปัจจุบันโดยอัตโนมัติ
        this.trackUserBehavior('copy_paste', { copiedLength });
      } catch {
        // เงียบไว้
      }
    };

    document.addEventListener('copy', onCopy, { passive: true });
    this.copyListenerBound = true;
  }
  /**
   * Check if user is currently signed in
   */
  private isUserSignedIn(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    try {
      const inviteData = localStorage.getItem('inviteData');
      if (!inviteData) {
        return false;
      }

      const parsed = JSON.parse(inviteData);
      if (!parsed?.data?.invitedToken) {
        return false;
      }

      // Check if token is still valid (not expired)
      if (parsed.data.expiredAt) {
        const expiredAt = new Date(parsed.data.expiredAt);
        const now = new Date();
        const isValid = now <= expiredAt;
        return isValid;
      }
      return true;
    } catch (error) {
      console.error('❌ Analytics: Error checking user sign-in status:', error);
      return false;
    }
  }

  /**
   * Get user ID from stored invite data
   */
  private getUserId(): string | null {
    // console.log('🔍 Analytics: getUserId called');

    if (typeof localStorage === 'undefined') {
      // console.log('❌ Analytics: localStorage not available in getUserId');
      return null;
    }

    try {
      const inviteData = localStorage.getItem('inviteData');
      // console.log('🔍 Analytics: inviteData in getUserId:', inviteData);

      if (!inviteData) {
        // console.log('❌ Analytics: No inviteData in getUserId');
        return null;
      }

      const parsed = JSON.parse(inviteData);
      const userId = parsed?.data?.email || parsed?.data?.invitedToken || null;
      // console.log('🔍 Analytics: Parsed userId:', userId);

      return userId;
    } catch (error) {
      console.error('❌ Analytics: Error getting user ID:', error);
      return null;
    }
  }

  /**
   * Get candidate email from stored invite data
   */
  private getEmail(): string | null {
    try {
      const inviteData = localStorage.getItem('inviteData');
      if (!inviteData) return null;
      const parsed = JSON.parse(inviteData);
      return parsed?.data?.email || null;
    } catch (error) {
      console.error('❌ Analytics: Error getting email:', error);
      return null;
    }
  }

  /**
   * Get test set ID from stored invite data
   */
  private getTestSetId(): string | null {
    if (typeof localStorage === 'undefined') {
      const fromUrl = this.parseTestSetIdFromUrl();
      if (fromUrl) {
        //
      }
      return fromUrl ?? null;
    }

    try {
      const inviteData = localStorage.getItem('inviteData');
      let testSetId: string | null = null;
      if (inviteData) {
        const parsed = JSON.parse(inviteData);
        testSetId = parsed?.data?.testSetId || null;
      }

      // Fallback: parse from URL if not present in inviteData
      if (!testSetId) {
        const fromUrl = this.parseTestSetIdFromUrl();
        if (fromUrl) {
          testSetId = fromUrl;
        }
      }
      return testSetId;
    } catch (error) {
      console.error('❌ Analytics: Error getting test set ID:', error);
      // As last resort, try from URL
      return this.parseTestSetIdFromUrl();
    }
  }

  private parseTestSetIdFromUrl(): string | null {
    try {
      const path = window.location.pathname; // e.g. /exam/<testSetId>/...
      const parts = path.split('/').filter(Boolean);
      const examIndex = parts.indexOf('exam');
      if (examIndex !== -1 && parts.length > examIndex + 1) {
        return parts[examIndex + 1];
      }
      return null;
    } catch {
      return null;
    }
  }

  private async loadClarity(tagId = 'spv50f8e7o') {
    if ((window as any).clarity) return;
    (window as any).clarity = function () {
      (window as any).clarity.q = (window as any).clarity.q || [];
      // eslint-disable-next-line prefer-rest-params
      (window as any).clarity.q.push(arguments);
    };
    (window as any).clarity('consent', false);
    await new Promise<void>(res => {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.clarity.ms/tag/${tagId}`;
      s.onload = () => res();
      document.head.appendChild(s);
    });
  }
  /**
   * Wait for Clarity to be available
   */
  private waitForClarity(maxWaitTime = 10000): Promise<boolean> {
    return new Promise(resolve => {
      let waited = 0;
      const step = 100;
      const ready = () =>
        typeof (window as any).clarity === 'function' &&
        !!(window as any).clarity.v; // <-- ของจริงจะมี .v

      const tick = () => {
        if (ready()) return resolve(true);
        waited += step;
        if (waited >= maxWaitTime) return resolve(false);
        setTimeout(tick, step);
      };
      tick();
    });
  }

  // private isLocalhost(): boolean {
  //   return ['localhost', '127.0.0.1'].includes(location.hostname);
  // }
  /**
   * Configure gtag and Clarity with user authentication status
   */
  async configureUserTracking(): Promise<void> {
    this.bindGlobalCopyListener();
    if (typeof gtag === 'undefined') {
      //
    } else {
      this.configureGoogleAnalytics();
    }
    if (this.isExamSession() && this.isUserSignedIn()) {
      await this.loadClarity();
      const ok = await this.waitForClarity();
      if (ok) {
        try {
          this.startClarityOnce();
        } catch {
          // Intentionally left blank: Clarity may already be started
        }
        this.configureClarityWithExamData();
      }
    } else if (this.isExamSession()) {
      await this.loadClarity();
      const ok = await this.waitForClarity();
      if (ok) {
        try {
          this.startClarityOnce();
        } catch {
          // Intentionally left blank: Clarity may already be started
        }
        this.configureClarityWithExamData();
      }
    }
  }

  /**
   * Configure Google Analytics
   */
  private configureGoogleAnalytics(): void {
    if (this.isUserSignedIn()) {
      const userId = this.getUserId();
      const testSetId = this.getTestSetId();
      // console.log(
      //   '✅ Analytics: User signed in, setting user_id:',
      //   userId,
      //   'test_set_id:',
      //   testSetId
      // );

      gtag('config', this.TAG_ID, {
        user_id: userId,
        custom_map: {
          test_set_id: 'test_set_id',
        },
      });

      // Set test set ID if available
      if (testSetId) {
        gtag('set', 'test_set_id', testSetId);
      }
    } else {
      // console.log('Analytics: User not signed in, clearing user_id');
      gtag('config', this.TAG_ID, {
        user_id: null,
      });
    }
  }

  /**
   * Check if current session is an exam session
   */
  private isExamSession(): boolean {
    try {
      const path = window.location.pathname;
      const isExamRoute = /^\/exam(\/|$)/.test(path);
      // If URL indicates exam, consider it an exam session regardless of stored data
      if (isExamRoute) {
        return true;
      }
      const inviteData = localStorage.getItem('inviteData');
      if (!inviteData) return false;
      const parsed = JSON.parse(inviteData);
      // ตรวจสอบว่าเป็น exam session หรือไม่
      const hasExamData = parsed?.data?.testSetId || parsed?.data?.invitedToken;
      return !!hasExamData && isExamRoute;
    } catch (error) {
      console.error('❌ Analytics: Error checking exam session:', error);
      return false;
    }
  }

  /**
   * Configure Clarity with exam-specific data
   */
  private configureClarityWithExamData(): void {
    const userId = this.getUserId();
    const testSetId = this.getTestSetId();
    const email = this.getEmail();

    // Set exam-specific identification in Clarity
    if (userId) {
      clarity('set', 'userId', userId);
      clarity('set', 'userType', 'exam_candidate');
      clarity('set', 'sessionType', 'exam_session');
    }

    if (email) {
      clarity('set', 'email', email);
    }

    if (testSetId) {
      clarity('set', 'testSetId', testSetId);
      clarity('set', 'examType', 'coding_test');
    }

    // Set additional exam-specific tags
    clarity('set', 'appVersion', '1.0.0');
    clarity('set', 'timestamp', new Date().toISOString());
    clarity('set', 'examStartTime', new Date().toISOString());

    // Track exam session start
    clarity('event', 'exam_session_start', {
      userId,
      testSetId,
      email,
      sessionType: 'exam_session',
      examType: 'coding_test',
      timestamp: new Date().toISOString(),
    });

    // console.log('✅ Analytics: Clarity configured for exam session');
  }

  /**
   * Track user sign in (called when email is confirmed)
   */
  trackUserSignIn(email?: string): void {
    if (typeof gtag !== 'undefined') {
      const userId = email || this.getUserId();
      const testSetId = this.getTestSetId();
      // console.log('Tracking user sign in:', userId, 'test_set_id:', testSetId);

      gtag('config', this.TAG_ID, {
        user_id: userId,
      });

      gtag('event', 'exam_session_start', {
        email: userId,
        test_set_id: testSetId,
        event_category: 'engagement',
        event_label: 'email_confirmed',
      });
    }

    // Track in Clarity - เฉพาะ exam sessions
    if (typeof clarity !== 'undefined' && this.isExamSession()) {
      const userId = email || this.getUserId();
      const testSetId = this.getTestSetId();
      const userEmail = email || this.getEmail();

      clarity('event', 'exam_session_start', {
        userId,
        testSetId,
        email: userEmail,
        sessionType: 'exam_session',
        examType: 'coding_test',
        timestamp: new Date().toISOString(),
      });

      clarity('identify', userId, {
        email: userEmail,
        testSetId: testSetId,
      });
    }
  }

  /**
   * Track user sign out (called when localStorage is cleared)
   */
  trackUserSignOut(): void {
    if (typeof gtag !== 'undefined') {
      const userId = this.getUserId();
      const testSetId = this.getTestSetId();
      console.log('Tracking user sign out:', userId, 'test_set_id:', testSetId);

      gtag('config', this.TAG_ID, {
        user_id: null,
      });

      gtag('event', 'exam_session_end', {
        email: userId,
        test_set_id: testSetId,
        event_category: 'engagement',
        event_label: 'user_done_exam',
      });
    }

    // Track in Clarity - เฉพาะ exam sessions
    if (typeof clarity !== 'undefined' && this.isExamSession()) {
      const userId = this.getUserId();
      const testSetId = this.getTestSetId();
      const email = this.getEmail();

      clarity('event', 'exam_session_end', {
        userId,
        testSetId,
        email,
        sessionType: 'exam_session',
        examType: 'coding_test',
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track exam-specific events
   */
  trackExamEvent(
    eventName: string,
    parameters: Record<string, any> = {}
  ): void {
    // console.log('🔍 Analytics: trackExamEvent called:', eventName, parameters);

    const userId = this.getUserId();
    const testSetId = this.getTestSetId();
    const email = this.getEmail();
    const eventData = {
      event_category: 'exam',
      // Do not include 'email' here to avoid duplicate keys when spreading into Clarity payloads
      test_set_id: testSetId,
      timestamp: new Date().toISOString(),
      session_type: 'exam_session',
      exam_type: 'coding_test',
      ...parameters,
    };

    // Track in Google Analytics
    if (typeof gtag !== 'undefined') {
      // console.log('📊 Analytics: Sending event to GA4:', eventName, eventData);
      gtag('event', eventName, { ...eventData, email: userId });
    } else {
      // console.warn('❌ Analytics: gtag not available for event:', eventName);
    }

    // Track in Clarity - เฉพาะ exam sessions
    if (typeof clarity !== 'undefined' && this.isExamSession()) {
      clarity('event', eventName, {
        userId,
        testSetId,
        email,
        sessionType: 'exam_session',
        examType: 'coding_test',
        ...eventData,
      });
    } else {
      // console.log(
      //   'ℹ️ Analytics: Clarity not available or not exam session (likely blocked by ad blocker)'
      // );
    }

    // Fallback: Store in localStorage if no analytics available
    if (typeof gtag === 'undefined' && typeof clarity === 'undefined') {
      // console.log('📝 Analytics: Storing event in localStorage as fallback');
      this.fallbackToLocalStorage(eventName, eventData);
    }
  }

  /**
   * Track problem-specific attempts and actions
   */
  trackProblemAttempt(
    problemId: string,
    action: 'start' | 'test' | 'submit' | 'view',
    metadata: Record<string, any> = {}
  ): void {
    this.trackExamEvent(`problem_${action}`, {
      problem_id: problemId,
      action_type: action,
      ...metadata,
    });
  }

  /**
   * Track exam start
   */
  trackExamStarted(): void {
    // console.log('🔍 Analytics: trackExamStarted called');
    this.trackExamEvent('exam_started', {
      start_time: new Date().toISOString(),
      exam_type: 'coding_test',
    });
  }

  /**
   * Track exam completion
   */
  trackExamCompleted(
    completionMethod: 'manual' | 'time_expired' | 'force_submit'
  ): void {
    this.trackExamEvent('exam_completed', {
      completion_method: completionMethod,
      completion_time: new Date().toISOString(),
      exam_type: 'coding_test',
    });
  }

  /**
   * Track code testing
   */
  trackCodeTested(
    problemId: string,
    testResults: {
      passed: number;
      total: number;
      executionTime?: number;
      language?: string;
    }
  ): void {
    this.trackProblemAttempt(problemId, 'test', {
      test_cases_passed: testResults.passed,
      test_cases_total: testResults.total,
      execution_time: testResults.executionTime,
      programming_language: testResults.language,
      success_rate:
        testResults.total > 0
          ? (testResults.passed / testResults.total) * 100
          : 0,
    });
  }

  /**
   * Track problem submission
   */
  trackProblemSubmitted(
    problemId: string,
    submissionData: {
      score: number;
      timeUsed: number;
      attempts: number;
      language: string;
    }
  ): void {
    this.trackProblemAttempt(problemId, 'submit', {
      score: submissionData.score,
      time_used: submissionData.timeUsed,
      attempts: submissionData.attempts,
      programming_language: submissionData.language,
      submission_time: new Date().toISOString(),
    });
  }

  /**
   * Track problem viewing
   */
  trackProblemViewed(
    problemId: string,
    problemTitle: string,
    language: string
  ): void {
    this.trackProblemAttempt(problemId, 'view', {
      problem_title: problemTitle,
      programming_language: language,
      view_time: new Date().toISOString(),
    });
  }

  /**
   * Track time spent on problems
   */
  trackTimeSpent(problemId: string, timeSpent: number, action: string): void {
    this.trackExamEvent('time_tracking', {
      problem_id: problemId,
      time_spent_seconds: timeSpent,
      action: action,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track user behavior patterns
   */
  trackUserBehavior(
    behavior:
      | 'tab_switch'
      | 'copy_paste'
      | 'multiple_submissions'
      | 'rapid_testing',
    metadata: Record<string, any> = {}
  ): void {
    const eventName = `behavior_${behavior}`;
    // ส่งเข้า GA4 เป็นอีเวนต์เฉพาะ
    this.trackExamEvent(eventName, {
      // ไม่ต้องส่ง behavior_type แล้ว เพราะชื่ออีเวนต์สื่อความหมายอยู่แล้ว
      ...metadata,
    });

    // ส่งเข้า Clarity เป็นอีเวนต์เฉพาะ (เฉพาะตอน exam session)
    if (typeof clarity !== 'undefined' && this.isExamSession()) {
      const userId = this.getUserId();
      const testSetId = this.getTestSetId();

      clarity('event', eventName, {
        userId,
        testSetId,
        sessionType: 'exam_session',
        examType: 'coding_test',
        timestamp: new Date().toISOString(),
        ...metadata,
      });
    }
  }

  /**
   * Fallback method to store analytics data in localStorage
   */
  private fallbackToLocalStorage(
    eventName: string,
    eventData: Record<string, any>
  ): void {
    try {
      const analyticsKey = 'analytics_fallback_data';
      const existingData = localStorage.getItem(analyticsKey);
      const analyticsData = existingData ? JSON.parse(existingData) : [];

      analyticsData.push({
        event: eventName,
        data: eventData,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });

      // Keep only last 100 events to prevent localStorage overflow
      if (analyticsData.length > 100) {
        analyticsData.splice(0, analyticsData.length - 100);
      }

      localStorage.setItem(analyticsKey, JSON.stringify(analyticsData));
      // console.log('📝 Analytics: Event stored in localStorage fallback');
    } catch (error) {
      console.error(
        '❌ Analytics: Failed to store event in localStorage:',
        error
      );
    }
  }

  /**
   * Get fallback analytics data from localStorage
   */
  getFallbackAnalyticsData(): any[] {
    try {
      const analyticsKey = 'analytics_fallback_data';
      const data = localStorage.getItem(analyticsKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('❌ Analytics: Failed to get fallback data:', error);
      return [];
    }
  }

  /**
   * Clear fallback analytics data from localStorage
   */
  clearFallbackAnalyticsData(): void {
    try {
      localStorage.removeItem('analytics_fallback_data');
      // console.log('🗑️ Analytics: Fallback data cleared');
    } catch (error) {
      console.error('❌ Analytics: Failed to clear fallback data:', error);
    }
  }

  /**
   * Health check for analytics services
   */
  getAnalyticsStatus(): {
    gtag: boolean;
    clarity: boolean;
    userSignedIn: boolean;
    userId: string | null;
    testSetId: string | null;
    isExamSession: boolean;
  } {
    return {
      gtag: typeof gtag !== 'undefined',
      clarity: typeof clarity !== 'undefined',
      userSignedIn: this.isUserSignedIn(),
      userId: this.getUserId(),
      testSetId: this.getTestSetId(),
      isExamSession: this.isExamSession(),
    };
  }

  /**
   * Force initialize Clarity if it's available
   */
  async forceClarityInitialization(): Promise<boolean> {
    const status = this.getAnalyticsStatus();
    // console.log('📊 Analytics Status:', status);
    if (!status.clarity) {
      // console.warn(
      //   '❌ Analytics: Clarity not available for force initialization'
      // );
      return false;
    }
    if (status.userSignedIn && this.isExamSession()) {
      this.configureClarityWithExamData();
      // console.log(
      //   '✅ Analytics: Clarity force initialization completed for exam session'
      // );
      return true;
    } else {
      // console.log(
      //   'ℹ️ Analytics: User not signed in for exam or not exam session, skipping Clarity configuration'
      // );
      return false;
    }
  }

  /**
   * Debug method to test Clarity connection
   */
  testClarityConnection(): void {
    if (typeof clarity === 'undefined') {
      // console.error('❌ Clarity: Not available');
      return;
    }
    try {
      // Send a test event
      clarity('event', 'test_connection', {
        timestamp: new Date().toISOString(),
        source: 'debug_test',
      });
      // console.log('✅ Clarity: Test event sent successfully');
    } catch (error) {
      console.error('❌ Clarity: Failed to send test event:', error);
    }
  }
}
